import { Fragment, useId, useState, type ReactNode } from "react";
import {
  supportFaqCategories,
  type FaqBlock,
  type FaqCategory,
} from "lib/supportFaqData";

function navButtonClass(active: boolean) {
  const shell =
    "rounded-xl border px-5 py-2.5 text-sm font-bold transition-all sm:px-6 sm:py-3 sm:text-base";
  if (!active) {
    return `${shell} border-[var(--dash-border)] bg-[var(--dash-input)] text-[var(--dash-text-muted)] hover:border-[#58a6ff] hover:text-[var(--dash-text-strong)]`;
  }
  return `${shell} border-[#229ED9] bg-[#229ED9] text-white hover:border-[#229ED9] hover:text-white`;
}

function linkifyParagraph(text: string): ReactNode[] {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all font-medium text-[#58a6ff] underline decoration-[#58a6ff]/50 underline-offset-2 hover:text-[var(--dash-text-strong)]"
        >
          {part}
        </a>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function FaqAnswerBlocks({ blocks }: { blocks: FaqBlock[] }) {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-[var(--dash-text)] md:text-base">
      {blocks.map((block, i) => {
        if (block.type === "h") {
          return (
            <p key={i} className="pt-1 text-sm font-semibold text-[var(--dash-text-strong)] md:text-base">
              {block.text}
            </p>
          );
        }
        if (block.type === "p") {
          return (
            <p key={i} className="whitespace-pre-wrap">
              {linkifyParagraph(block.text)}
            </p>
          );
        }
        return (
          <ul key={i} className="list-disc space-y-1.5 pl-5 marker:text-[var(--dash-text-muted)]">
            {block.items.map((item, j) => (
              <li key={j} className="whitespace-pre-wrap pl-0.5">
                {linkifyParagraph(item)}
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
}

function FaqAccordionItem({
  categoryId,
  index,
  question,
  blocks,
  openKey,
  setOpenKey,
}: {
  categoryId: string;
  index: number;
  question: string;
  blocks: FaqBlock[];
  openKey: string | null;
  setOpenKey: (k: string | null) => void;
}) {
  const itemKey = `${categoryId}-${index}`;
  const open = openKey === itemKey;
  const panelId = useId();
  const buttonId = useId();

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
      <button
        id={buttonId}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpenKey(open ? null : itemKey)}
        className="flex w-full items-start justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-[var(--dash-surface-hover)] md:px-5 md:py-4"
      >
        <span className="text-sm font-semibold text-[var(--dash-text-strong)] md:text-base">{question}</span>
        <span
          className={`mt-0.5 shrink-0 text-[var(--dash-text-muted)] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        >
          <i className="fas fa-chevron-down text-xs sm:text-sm" />
        </span>
      </button>
      {open && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="border-t border-[var(--dash-border)] bg-[var(--dash-input)] px-4 py-4 md:px-5 md:py-5"
        >
          <FaqAnswerBlocks blocks={blocks} />
        </div>
      )}
    </div>
  );
}

export default function SupportFaqSection() {
  const [activeCategory, setActiveCategory] = useState<FaqCategory["id"]>(
    supportFaqCategories[0].id,
  );
  const [openKey, setOpenKey] = useState<string | null>(null);

  const category = supportFaqCategories.find((c) => c.id === activeCategory)!;

  const onTabChange = (id: FaqCategory["id"]) => {
    setActiveCategory(id);
    setOpenKey(null);
  };

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-wrap justify-center gap-3 sm:gap-4">
        {supportFaqCategories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => onTabChange(cat.id)}
            className={navButtonClass(cat.id === activeCategory)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3">
        {category.items.map((item, index) => (
          <FaqAccordionItem
            key={`${category.id}-${index}`}
            categoryId={category.id}
            index={index}
            question={item.question}
            blocks={item.blocks}
            openKey={openKey}
            setOpenKey={setOpenKey}
          />
        ))}
      </div>
    </div>
  );
}
